"use client"

import React, { useState, useEffect } from "react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import { billingAPI, EmailTemplate } from "../services/billingService"
import PageHeader from "../components/ui/dashboard/PageHeader"
import { Plus, Search, Mail, Edit, Trash2, Eye, Code, Send } from "lucide-react"
import {
    Tabs, Card, Flex, Text, Button, Badge, Table, Dialog, TextField, Select, TextArea, Heading
} from "@radix-ui/themes"

const EmailTemplatesPage: React.FC = () => {
    const { t } = useLanguage()
    const { toast } = useToast()
    const [activeTab, setActiveTab] = useState("templates")

    // State
    const [templates, setTemplates] = useState<EmailTemplate[]>([])
    const [loading, setLoading] = useState(true)
    const [showDialog, setShowDialog] = useState(false)
    const [showPreviewDialog, setShowPreviewDialog] = useState(false)
    const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null)
    const [previewTemplate, setPreviewTemplate] = useState<EmailTemplate | null>(null)
    const [searchQuery, setSearchQuery] = useState("")

    // Test Email State
    const [testEmail, setTestEmail] = useState("")
    const [sendingTest, setSendingTest] = useState(false)

    // Form State
    const [formData, setFormData] = useState({
        name: "",
        type: "invoice" as "invoice" | "receipt" | "payment_confirmation" | "reminder" | "cancellation",
        subject: "",
        body: "",
        isActive: true
    })

    // Available template variables
    const templateVariables = [
        "{{restaurant_name}}",
        "{{plan_name}}",
        "{{amount}}",
        "{{invoice_number}}",
        "{{payment_date}}",
        "{{due_date}}",
        "{{start_date}}",
        "{{end_date}}",
        "{{payment_reference}}",
        "{{support_email}}"
    ]

    // Fetch Templates
    const fetchTemplates = async () => {
        setLoading(true)
        try {
            const response = await billingAPI.getEmailTemplates()
            setTemplates(response.data.data || [])
        } catch (error) {
            console.error("Failed to load email templates", error)
            // Mock data for development
            setTemplates([
                {
                    _id: "1",
                    name: "Monthly Invoice",
                    type: "invoice",
                    subject: "Invoice for {{restaurant_name}} - {{plan_name}}",
                    body: `<h2>Invoice</h2><p>Dear {{restaurant_name}},</p><p>Your invoice for {{plan_name}} subscription is ready.</p><p><strong>Amount: {{amount}} SDG</strong></p><p>Due Date: {{due_date}}</p>`,
                    variables: templateVariables,
                    isActive: true,
                    createdAt: "2024-01-01",
                    updatedAt: "2024-01-01"
                },
                {
                    _id: "2",
                    name: "Payment Receipt",
                    type: "receipt",
                    subject: "Payment Received - {{restaurant_name}}",
                    body: `<h2>Payment Receipt</h2><p>Dear {{restaurant_name}},</p><p>We have received your payment of {{amount}} SDG.</p><p>Reference: {{payment_reference}}</p><p>Thank you!</p>`,
                    variables: templateVariables,
                    isActive: true,
                    createdAt: "2024-01-01",
                    updatedAt: "2024-01-01"
                },
                {
                    _id: "3",
                    name: "Payment Confirmation",
                    type: "payment_confirmation",
                    subject: "Payment Confirmed - {{restaurant_name}}",
                    body: `<h2>Payment Confirmation</h2><p>Dear {{restaurant_name}},</p><p>Your payment has been confirmed and your subscription is now active.</p><p>Amount: {{amount}} SDG</p><p>Valid until: {{end_date}}</p>`,
                    variables: templateVariables,
                    isActive: true,
                    createdAt: "2024-01-01",
                    updatedAt: "2024-01-01"
                }
            ])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchTemplates()
    }, [])

    const handleSave = async () => {
        try {
            if (selectedTemplate) {
                await billingAPI.updateEmailTemplate(selectedTemplate._id, formData)
                toast({ title: t("success"), description: "Template updated successfully" })
            } else {
                await billingAPI.createEmailTemplate({
                    ...formData,
                    variables: templateVariables
                })
                toast({ title: t("success"), description: "Template created successfully" })
            }
            setShowDialog(false)
            resetForm()
            fetchTemplates()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to save template", variant: "destructive" })
        }
    }

    const handleDelete = async (id: string) => {
        if (!window.confirm("Are you sure you want to delete this template?")) return
        try {
            await billingAPI.deleteEmailTemplate(id)
            toast({ title: t("success"), description: "Template deleted successfully" })
            fetchTemplates()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to delete template", variant: "destructive" })
        }
    }

    const handleEdit = (template: EmailTemplate) => {
        setSelectedTemplate(template)
        setFormData({
            name: template.name,
            type: template.type,
            subject: template.subject,
            body: template.body,
            isActive: template.isActive
        })
        setShowDialog(true)
    }

    const handleAddNew = () => {
        setSelectedTemplate(null)
        resetForm()
        setShowDialog(true)
    }

    const handlePreview = (template: EmailTemplate) => {
        setPreviewTemplate(template)
        setTestEmail("") // Reset test email input
        setShowPreviewDialog(true)
    }

    const handleSendTestEmail = async () => {
        if (!testEmail || !previewTemplate) return

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(testEmail)) {
            toast({ title: t("error"), description: t("invalidEmail") || "Please enter a valid email address", variant: "destructive" })
            return
        }

        setSendingTest(true)
        try {
            await billingAPI.sendTestEmailTemplate(previewTemplate._id, testEmail)
            toast({ title: t("success"), description: t("testEmailSent") || "Test email sent successfully" })
        } catch (error) {
            toast({ title: t("error"), description: t("failedToSendTestEmail") || "Failed to send test email", variant: "destructive" })
        } finally {
            setSendingTest(false)
        }
    }

    const resetForm = () => {
        setFormData({
            name: "",
            type: "invoice",
            subject: "",
            body: "",
            isActive: true
        })
    }

    const insertVariable = (variable: string) => {
        setFormData(prev => ({
            ...prev,
            body: prev.body + " " + variable
        }))
    }

    const getTypeColor = (type: string) => {
        const colors: Record<string, any> = {
            invoice: "blue",
            receipt: "green",
            payment_confirmation: "purple",
            reminder: "orange",
            cancellation: "red"
        }
        return colors[type] || "gray"
    }

    const filteredTemplates = templates.filter(template =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.type.toLowerCase().includes(searchQuery.toLowerCase())
    )

    return (
        <div className="space-y-6">
            <PageHeader
                title={t("emailTemplates") || "Email Templates"}
                subtitle={t("manageEmailTemplatesDesc") || "Manage automated billing email templates"}
            />

            <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
                <Tabs.List>
                    <Tabs.Trigger value="templates">{t("templates") || "Templates"}</Tabs.Trigger>
                    <Tabs.Trigger value="variables">{t("variables") || "Available Variables"}</Tabs.Trigger>
                </Tabs.List>

                <div className="mt-4">
                    <Tabs.Content value="templates">
                        <Card>
                            <Flex direction="column" gap="4">
                                <Flex justify="between" align="center">
                                    <div className="relative w-64">
                                        <Search className="absolute top-1/2 -translate-y-1/2 left-3 w-4 h-4 text-gray-400" />
                                        <input
                                            placeholder={t("searchTemplates") || "Search templates..."}
                                            className="w-full py-2 pl-10 pr-4 border rounded-lg border-gray-300 dark:border-gray-600 bg-transparent"
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                    </div>
                                    <Button onClick={handleAddNew}>
                                        <Plus className="w-4 h-4 mr-2" /> {t("addTemplate") || "Add Template"}
                                    </Button>
                                </Flex>

                                <Table.Root variant="surface">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColumnHeaderCell>{t("name") || "Name"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("type") || "Type"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("subject") || "Subject"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("status") || "Status"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("actions") || "Actions"}</Table.ColumnHeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {filteredTemplates.map(template => (
                                            <Table.Row key={template._id}>
                                                <Table.Cell>
                                                    <Flex align="center" gap="2">
                                                        <Mail className="w-4 h-4 text-gray-400" />
                                                        <Text weight="bold">{template.name}</Text>
                                                    </Flex>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={getTypeColor(template.type)}>
                                                        {template.type.replace('_', ' ')}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Text size="2">{template.subject}</Text>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={template.isActive ? "green" : "gray"}>
                                                        {template.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Flex gap="2">
                                                        <Button size="1" variant="soft" onClick={() => handlePreview(template)}>
                                                            <Eye className="w-3 h-3" />
                                                        </Button>
                                                        <Button size="1" variant="soft" color="blue" onClick={() => handleEdit(template)}>
                                                            <Edit className="w-3 h-3" />
                                                        </Button>
                                                        <Button size="1" variant="soft" color="red" onClick={() => handleDelete(template._id)}>
                                                            <Trash2 className="w-3 h-3" />
                                                        </Button>
                                                    </Flex>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                        {filteredTemplates.length === 0 && !loading && (
                                            <Table.Row>
                                                <Table.Cell colSpan={5} align="center">
                                                    <Text color="gray">{t("noTemplatesFound") || "No templates found"}</Text>
                                                </Table.Cell>
                                            </Table.Row>
                                        )}
                                    </Table.Body>
                                </Table.Root>
                            </Flex>
                        </Card>
                    </Tabs.Content>

                    <Tabs.Content value="variables">
                        <Card>
                            <Flex direction="column" gap="4" p="4">
                                <Heading size="4">{t("availableVariables") || "Available Template Variables"}</Heading>
                                <Text size="2" color="gray">
                                    {t("variablesDesc") || "Use these variables in your email templates. They will be replaced with actual values when sending emails."}
                                </Text>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                    {templateVariables.map(variable => (
                                        <Card key={variable} className="p-3">
                                            <Flex align="center" gap="2">
                                                <Code className="w-4 h-4 text-blue-500" />
                                                <code className="text-sm font-mono">{variable}</code>
                                            </Flex>
                                        </Card>
                                    ))}
                                </div>
                            </Flex>
                        </Card>
                    </Tabs.Content>
                </div>
            </Tabs.Root>

            {/* Template Dialog */}
            <Dialog.Root open={showDialog} onOpenChange={setShowDialog}>
                <Dialog.Content style={{ maxWidth: 700, maxHeight: "90vh", overflow: "auto" }}>
                    <Dialog.Title>{selectedTemplate ? (t("editTemplate") || "Edit Template") : (t("addTemplate") || "Add Template")}</Dialog.Title>
                    <Flex direction="column" gap="3">
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("templateName") || "Template Name"}</Text>
                            <TextField.Root value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Monthly Invoice" required />
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("type") || "Type"}</Text>
                            <Select.Root value={formData.type} onValueChange={(value: any) => setFormData({ ...formData, type: value })}>
                                <Select.Trigger style={{ width: "100%" }} />
                                <Select.Content>
                                    <Select.Item value="invoice">Invoice</Select.Item>
                                    <Select.Item value="receipt">Receipt</Select.Item>
                                    <Select.Item value="payment_confirmation">Payment Confirmation</Select.Item>
                                    <Select.Item value="reminder">Reminder</Select.Item>
                                    <Select.Item value="cancellation">Cancellation</Select.Item>
                                </Select.Content>
                            </Select.Root>
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("subject") || "Email Subject"}</Text>
                            <TextField.Root value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} placeholder="Invoice for {{restaurant_name}}" required />
                        </label>
                        <div>
                            <Flex justify="between" align="center" mb="2">
                                <Text as="div" size="2" weight="bold">{t("emailBody") || "Email Body (HTML)"}</Text>
                                <Text size="1" color="gray">{t("insertVariables") || "Insert variables:"}</Text>
                            </Flex>
                            <Flex gap="1" mb="2" wrap="wrap">
                                {templateVariables.slice(0, 6).map(variable => (
                                    <Button key={variable} size="1" variant="soft" onClick={() => insertVariable(variable)}>
                                        {variable}
                                    </Button>
                                ))}
                            </Flex>
                            <textarea
                                value={formData.body}
                                onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                                placeholder="<h2>Invoice</h2><p>Dear {{restaurant_name}},</p>"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md resize-none dark:bg-gray-800 dark:text-white font-mono text-sm"
                                rows={10}
                                required
                            />
                        </div>
                        <Flex gap="3" mt="4" justify="end">
                            <Dialog.Close>
                                <Button variant="soft" color="gray">{t("cancel") || "Cancel"}</Button>
                            </Dialog.Close>
                            <Button onClick={handleSave}>{t("save") || "Save"}</Button>
                        </Flex>
                    </Flex>
                </Dialog.Content>
            </Dialog.Root>

            {/* Preview Dialog */}
            <Dialog.Root open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
                <Dialog.Content style={{ maxWidth: 600 }}>
                    <Dialog.Title>{t("templatePreview") || "Template Preview"}</Dialog.Title>
                    {previewTemplate && (
                        <Flex direction="column" gap="3">
                            <div>
                                <Text size="2" weight="bold" color="gray">{t("subject") || "Subject"}:</Text>
                                <Text size="2">{previewTemplate.subject}</Text>
                            </div>
                            <div>
                                <Text size="2" weight="bold" color="gray" mb="2">{t("body") || "Body"}:</Text>
                                <div
                                    className="p-4 border rounded bg-white dark:bg-gray-900"
                                    dangerouslySetInnerHTML={{ __html: previewTemplate.body }}
                                />
                            </div>

                            <Card variant="surface" className="mt-4 p-4 bg-gray-50 dark:bg-gray-800/50">
                                <Flex direction="column" gap="2">
                                    <Text size="2" weight="bold">{t("sendTestEmail") || "Send Test Email"}</Text>
                                    <Flex gap="2">
                                        <TextField.Root
                                            value={testEmail}
                                            onChange={(e) => setTestEmail(e.target.value)}
                                            placeholder="test@example.com"
                                            className="flex-1"
                                            type="email"
                                        />
                                        <Button onClick={handleSendTestEmail} disabled={sendingTest || !testEmail}>
                                            <Send className="w-4 h-4 mr-2" />
                                            {sendingTest ? t("sending") || "Sending..." : t("send") || "Send"}
                                        </Button>
                                    </Flex>
                                    <Text size="1" color="gray">
                                        {t("testEmailNote") || "A test email will be sent populated with dummy fallback data."}
                                    </Text>
                                </Flex>
                            </Card>

                            <Flex justify="end" mt="2">
                                <Dialog.Close>
                                    <Button variant="soft">{t("close") || "Close"}</Button>
                                </Dialog.Close>
                            </Flex>
                        </Flex>
                    )}
                </Dialog.Content>
            </Dialog.Root>
        </div>
    )
}

export default EmailTemplatesPage
